// Set in frontend/.env (local) or REACT_APP_API_URL at Docker/CI build
import { openReportLoadingTab } from "../utils/reportPdfLoader";

const _apiUrl = process.env.REACT_APP_API_URL;
const API_BASE =
  _apiUrl === "/" || _apiUrl === ""
    ? ""
    : // Prefer localhost over 127.0.0.1 so Chrome does not treat CRA→API as
      // a Private Network Access cross-address request during local login.
      (_apiUrl || "http://localhost:5001/").replace(/\/?$/, "");

export function joinPath(path) {
  const p = String(path || "").replace(/^\/+/, "");
  return `${API_BASE}/${p}`;
}

export async function apiGet(path, { surveyToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (surveyToken) headers["x-survey-token"] = surveyToken;
  const res = await fetch(joinPath(path), { headers });
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.code = json.code;
    err.payload = json;
    throw err;
  }
  return json;
}

export async function apiPost(path, body, { surveyToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (surveyToken) headers["x-survey-token"] = surveyToken;
  const res = await fetch(joinPath(path), {
    method: "POST",
    headers,
    body: JSON.stringify(body ?? {}),
  });
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.code = json.code;
    err.payload = json;
    throw err;
  }
  return json;
}

export async function apiPatch(path, body, { surveyToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (surveyToken) headers["x-survey-token"] = surveyToken;
  const res = await fetch(joinPath(path), {
    method: "PATCH",
    headers,
    body: JSON.stringify(body ?? {}),
  });
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.code = json.code;
    err.payload = json;
    throw err;
  }
  return json;
}

export function createHelpSession(surveyToken, context) {
  return apiPost("v2/ai/sessions", context ?? {}, { surveyToken });
}

export function updateHelpSessionContext(surveyToken, sessionId, context) {
  return apiPatch(`v2/ai/sessions/${sessionId}/context`, context ?? {}, {
    surveyToken,
  });
}

export function sendHelpMessage(surveyToken, sessionId, message, context) {
  const body = { message };
  if (context) body.context = context;
  return apiPost(`v2/ai/sessions/${sessionId}/messages`, body, { surveyToken });
}

export function fetchHelpNudge(surveyToken, context) {
  return apiPost("v2/ai/nudge", context ?? {}, { surveyToken });
}

export function fetchGuideFrame(surveyToken, context, { includeWelcome = false } = {}) {
  return apiPost(
    "v2/ai/guide/frame",
    { ...(context ?? {}), include_welcome: includeWelcome },
    { surveyToken }
  );
}

export function fetchUpgradeCopy(surveyToken, phase) {
  return apiGet(`v2/ai/upgrade-copy?phase=${encodeURIComponent(phase)}`, {
    surveyToken,
  });
}

export function loadAssessment(surveyToken) {
  return apiGet(`v2/assessments/${encodeURIComponent(surveyToken)}`, {
    surveyToken,
  });
}

export function submitAssessmentStep(surveyToken, step, responses) {
  return apiPost(
    `v2/assessments/${encodeURIComponent(surveyToken)}/steps/${step}`,
    { responses },
    { surveyToken }
  );
}

export function updateAssessmentStatus(surveyToken, status) {
  return apiPost(
    `v2/assessments/${encodeURIComponent(surveyToken)}/status`,
    { status },
    { surveyToken }
  );
}

export function upgradeToFullAssessment(surveyToken) {
  return apiPost(
    `v2/assessments/${encodeURIComponent(surveyToken)}/upgrade-full`,
    {},
    { surveyToken }
  );
}

export function declineFullAssessment(surveyToken) {
  return apiPost(
    `v2/assessments/${encodeURIComponent(surveyToken)}/decline-full`,
    {},
    { surveyToken }
  );
}

/** CEO company benchmark for full assessments. */
export function submitPreSurveyProfile(surveyToken, profile) {
  return apiPost(
    `v2/assessments/${encodeURIComponent(surveyToken)}/pre-survey`,
    profile,
    { surveyToken }
  );
}

/** LITE assessment PDF (after LITE completion; available during/after full assessment). */
export async function fetchLiteAssessmentReportPdf(surveyToken) {
  const headers = {};
  if (surveyToken) headers["x-survey-token"] = surveyToken;
  const res = await fetch(
    joinPath(`v2/assessments/${encodeURIComponent(surveyToken)}/lite-report`),
    { headers }
  );
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const json = await res.json();
      message = json.message || message;
    } catch {
      /* binary error body */
    }
    throw new Error(message);
  }
  return res.blob();
}

export async function openLiteAssessmentReportPdf(surveyToken) {
  const win = openReportLoadingTab("Generating lite report…");

  try {
    const blob = await fetchLiteAssessmentReportPdf(surveyToken);
    const url = URL.createObjectURL(blob);
    if (win) {
      win.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}
